import { useEffect } from 'react';

const TAPPABLE = 'a[href], button, summary, label, select, [role="button"], [role="link"], [role="tab"], [role="switch"], [role="checkbox"], [role="radio"], [role="option"], [role="menuitem"]';

/**
 * Catch-all press feedback (like React Aria usePress / iOS highlight): marks the pressed tappable
 * with data-pressed so globals.css can shrink it slightly — covers Plasmic divs with onClick too.
 */
export const PressFeedback = () => {
  useEffect(() => {
    let pressed: Element | null = null;

    let pressedAt = 0;
    // Keep the layer ~120ms so a quick tap is still visible (Material ripple minimum).
    const release = () => {
      const el = pressed;
      pressed = null;
      if (!el) return;
      const wait = Math.max(0, 120 - (performance.now() - pressedAt));
      setTimeout(() => el !== pressed && el.removeAttribute('data-pressed'), wait);
    };

    // Nearest real control; otherwise the outermost element of the cursor:pointer run (cursor is inherited).
    const findTarget = (el: Element | null) => {
      let node: Element | null = el;
      let pointerRoot: Element | null = null;
      while (node && node !== document.body) {
        if (node.matches(TAPPABLE)) return node;
        if (getComputedStyle(node).cursor === 'pointer') pointerRoot = node;
        else if (pointerRoot) return pointerRoot;
        node = node.parentElement;
      }
      return pointerRoot;
    };

    const onDown = (e: PointerEvent) => {
      if (e.button !== 0) return;
      release();
      const target = findTarget(e.target as Element);
      if (!target || (target as HTMLButtonElement).disabled || target.getAttribute('aria-disabled') === 'true') return;
      // Wide rows/cards shrink less so the edge movement stays ~4px.
      const width = target.getBoundingClientRect().width;
      (target as HTMLElement).style.setProperty('--press-scale', String(Math.max(0.97, 1 - 8 / Math.max(width, 1))));
      target.setAttribute('data-press-ready', '');
      pressed = target;
      pressedAt = performance.now();
      target.setAttribute('data-pressed', '');
    };

    window.addEventListener('pointerdown', onDown, { passive: true });
    window.addEventListener('pointerup', release, { passive: true });
    window.addEventListener('pointercancel', release, { passive: true });
    window.addEventListener('scroll', release, { passive: true, capture: true });
    return () => {
      window.removeEventListener('pointerdown', onDown);
      window.removeEventListener('pointerup', release);
      window.removeEventListener('pointercancel', release);
      window.removeEventListener('scroll', release, { capture: true });
    };
  }, []);

  return null;
};

export default PressFeedback;
